import type { MonthlyStatus } from '@/modules/projects/components/StatementCoverage/types'
import { accountNumbersText, NO_ACCOUNT_LABEL } from './accounts'
import { isUnfiledPile } from './fromProject'
import type { MemberCard } from './memberCardsOf'
import {
  filedMonthIndex,
  filedYear,
  formatMonth,
  MONTHS_IN_YEAR,
  monthPeriod,
} from './period'
import type { Account, DatePeriod, StatementView } from './types'

export interface MapCell {
  accountId: string | null
  period: DatePeriod
  monthIndex: number
  views: StatementView[]
  // What the live coverage says about a month no statement is drawn in (see coverage.ts).
  coverage: MonthlyStatus | null
}

// One statement drawn as a bar across the months it owns in one year row. A statement that
// crosses New Year is two bars, one per row, each flagged on the side it continues from or into.
export interface MapSpan {
  view: StatementView
  fromMonth: number
  toMonth: number
  continuesBefore: boolean
  continuesAfter: boolean
  // Bars that overlap sit on separate tracks, first track on top.
  track: number
}

export interface MapYear {
  year: number
  cells: MapCell[]
  spans: MapSpan[]
  trackCount: number
}

export interface MapLane {
  account: Account | null
  accountId: string | null
  // The primary card's number when this lane is a member card. Such a lane holds no statements.
  memberOf: string | null
  label: string
  years: MapYear[]
  statementCount: number
  pageCount: number
}

export interface SplitMap {
  lanes: MapLane[]
  unplaced: StatementView[]
}

// A calendar month as one number, so months compare and sort like integers.
export type MonthNumber = number

// The months a statement is drawn across. The live coverage grid decides this for a saved cycle;
// anything it does not know is drawn in the month it closes.
export type MonthsOwnedBy = (view: StatementView) => MonthNumber[]

const NO_ACCOUNT = ''

export const cellMonthName = (cell: MapCell) => formatMonth(cell.period.end)

export const monthNumber = (year: number, monthIndex: number): MonthNumber =>
  year * MONTHS_IN_YEAR + monthIndex

export const monthNumberOf = (date: Date) =>
  monthNumber(date.getFullYear(), date.getMonth())

const yearOf = (month: MonthNumber) => Math.floor(month / MONTHS_IN_YEAR)

const monthIndexOf = (month: MonthNumber) => month % MONTHS_IN_YEAR

export const filedMonth = (period: DatePeriod): MonthNumber =>
  monthNumber(filedYear(period), filedMonthIndex(period))

export const filedMonthOnly: MonthsOwnedBy = (view) =>
  view.statement.period ? [filedMonth(view.statement.period)] : []

// A statement is drawn in the lane of every account it covers, so a combined mailing is one bar
// in each of its accounts' lanes, the way the coverage page paints it.
const laneKeysOf = (view: StatementView, accounts: Account[]) => {
  const accountId = view.statement.accountId
  if (!accountId) return [NO_ACCOUNT]
  return (
    accounts.find((account) => account.id === accountId)?.numbers ?? [accountId]
  )
}

const byLane = (views: StatementView[], accounts: Account[]) => {
  const lanes = new Map<string, StatementView[]>()
  for (const view of views) {
    for (const key of laneKeysOf(view, accounts)) {
      lanes.set(key, [...(lanes.get(key) ?? []), view])
    }
  }
  return lanes
}

const isOneNumber = (account: Account) => account.numbers.length === 1

// Every named lane on the map is one number, so this is the account a lane stands for.
export const laneNumberOf = (lane: MapLane) => lane.account?.numbers[0] ?? null

interface OwnedRange {
  view: StatementView
  first: MonthNumber
  last: MonthNumber
}

// A bar runs from the first month a statement owns to the last, and always includes the month it
// closes in, so a cycle the coverage grid has not painted yet still lands where it is filed.
const ownedRange = (
  view: StatementView,
  monthsOwnedBy: MonthsOwnedBy,
): OwnedRange | null => {
  const period = view.statement.period
  if (!period) return null
  const months = [filedMonth(period), ...monthsOwnedBy(view)]
  return { view, first: Math.min(...months), last: Math.max(...months) }
}

// An account with no dated statement has no years: it is drawn as one strip to drop pages on.
const yearsSpanned = (months: MonthNumber[]) => {
  if (months.length === 0) return []
  const first = yearOf(Math.min(...months))
  const last = yearOf(Math.max(...months))
  return Array.from(
    { length: last - first + 1 },
    (_unused, step) => first + step,
  )
}

// Every month a range touches, so a cell knows which statements run through it.
const monthsIn = (range: OwnedRange): MonthNumber[] =>
  Array.from(
    { length: range.last - range.first + 1 },
    (_unused, step) => range.first + step,
  )

const viewsByMonth = (ranges: OwnedRange[]) => {
  const months = new Map<MonthNumber, StatementView[]>()
  for (const range of ranges) {
    for (const month of monthsIn(range)) {
      months.set(month, [...(months.get(month) ?? []), range.view])
    }
  }
  return months
}

type MonthsSpanned = Pick<MapSpan, 'fromMonth' | 'toMonth'>

export const overlaps = (one: MonthsSpanned, other: MonthsSpanned) =>
  one.fromMonth <= other.toMonth && other.fromMonth <= one.toMonth

// Longest bars first so a short one tucks under a long neighbour, never the other way round.
export const byStartThenLength = (one: MonthsSpanned, other: MonthsSpanned) =>
  one.fromMonth - other.fromMonth ||
  other.toMonth - other.fromMonth - (one.toMonth - one.fromMonth)

// Each item takes the first track no clashing item already placed is on.
export const placeOnTracks = <Item>(
  items: Item[],
  spanOf: (item: Item) => MapSpan,
  withTrack: (item: Item, track: number) => Item,
  clashes: (one: Item, other: Item) => boolean,
): Item[] => {
  const placed: Item[] = []
  const inOrder = [...items].sort((one, other) =>
    byStartThenLength(spanOf(one), spanOf(other)),
  )
  for (const item of inOrder) {
    const taken = new Set(
      placed
        .filter((other) => clashes(item, other))
        .map((other) => spanOf(other).track),
    )
    let track = 0
    while (taken.has(track)) track += 1
    placed.push(withTrack(item, track))
  }
  return placed
}

const spansOfYear = (ranges: OwnedRange[], year: number): MapSpan[] => {
  const yearStart = monthNumber(year, 0)
  const yearEnd = monthNumber(year, MONTHS_IN_YEAR - 1)
  const spans = ranges
    .filter((range) => range.first <= yearEnd && range.last >= yearStart)
    .map((range) => ({
      view: range.view,
      fromMonth: monthIndexOf(Math.max(range.first, yearStart)),
      toMonth: monthIndexOf(Math.min(range.last, yearEnd)),
      continuesBefore: range.first < yearStart,
      continuesAfter: range.last > yearEnd,
      track: 0,
    }))
  return placeOnTracks(
    spans,
    (span) => span,
    (span, track) => ({ ...span, track }),
    overlaps,
  )
}

const trackCountOf = (spans: MapSpan[]) =>
  spans.reduce((most, span) => Math.max(most, span.track + 1), 1)

const cellsOfYear = (
  accountId: string | null,
  year: number,
  held: ReadonlyMap<MonthNumber, StatementView[]>,
): MapCell[] =>
  Array.from({ length: MONTHS_IN_YEAR }, (_unused, monthIndex) => ({
    accountId,
    period: monthPeriod(year, monthIndex),
    monthIndex,
    views: held.get(monthNumber(year, monthIndex)) ?? [],
    coverage: null,
  }))

// The lane is named by the number the account prints. Its type and owner are shown beside
// that name, never joined into it.
const laneFor = (
  account: Account | null,
  views: StatementView[],
  monthsOwnedBy: MonthsOwnedBy,
): MapLane => {
  const accountId = account?.id ?? null
  const ranges = views
    .map((view) => ownedRange(view, monthsOwnedBy))
    .filter((range): range is OwnedRange => range !== null)
  const held = viewsByMonth(ranges)

  const years = yearsSpanned([...held.keys()]).map((year) => {
    const spans = spansOfYear(ranges, year)
    return {
      year,
      cells: cellsOfYear(accountId, year, held),
      spans,
      trackCount: trackCountOf(spans),
    }
  })

  return {
    account,
    accountId,
    memberOf: null,
    label: account ? accountNumbersText(account) : NO_ACCOUNT_LABEL,
    years,
    statementCount: views.length,
    pageCount: views.reduce((total, view) => total + view.pages.length, 0),
  }
}

const NOTHING_HELD = new Map<MonthNumber, StatementView[]>()

export const emptyCellsOfYear = (accountId: string | null, year: number) =>
  cellsOfYear(accountId, year, NOTHING_HELD)

// A member card's lane runs over the years it spent in. Its months stay empty: the coverage grid
// says which of them the card spent in.
const memberLaneFor = (card: MemberCard): MapLane => ({
  account: card.account,
  accountId: card.account.id,
  memberOf: card.parentNumber,
  label: accountNumbersText(card.account),
  years: yearsSpanned(card.months).map((year) => ({
    year,
    cells: emptyCellsOfYear(card.account.id, year),
    spans: [],
    trackCount: 1,
  })),
  statementCount: 0,
  pageCount: 0,
})

// Each member card sits right after its primary card, so the map reads the way the coverage page
// does. A card whose primary has no lane still shows, at the end.
// A card whose number already has a lane of its own is shown there, never twice.
const withMemberCards = (lanes: MapLane[], allCards: MemberCard[]) => {
  const numbers = new Set(lanes.map(laneNumberOf))
  const cards = allCards.filter((card) => !numbers.has(card.account.numbers[0]))
  const membersOf = (lane: MapLane) =>
    cards
      .filter((card) => card.parentNumber === laneNumberOf(lane))
      .map(memberLaneFor)
  return [
    ...lanes.flatMap((lane) => [lane, ...membersOf(lane)]),
    ...cards
      .filter((card) => !numbers.has(card.parentNumber))
      .map(memberLaneFor),
  ]
}

export const yearsOf = (lanes: MapLane[]) =>
  [...new Set(lanes.flatMap((lane) => lane.years.map((row) => row.year)))].sort(
    (one, other) => one - other,
  )

export const accountLanes = (lanes: MapLane[]) =>
  lanes.filter((lane) => lane.memberOf === null)

export const memberLanes = (lanes: MapLane[]) =>
  lanes.filter((lane) => lane.memberOf !== null)

export interface HiddenLanes {
  accounts: number
  memberCards: number
}

export const hiddenLanes = (all: MapLane[], shown: MapLane[]): HiddenLanes => ({
  accounts: accountLanes(all).length - accountLanes(shown).length,
  memberCards: memberLanes(all).length - memberLanes(shown).length,
})

// One lane per account number, one row per year, one bar per statement across the months it
// owns: where every statement in the file was filed, and nothing about whether that is right.
export const buildMap = (
  accounts: Account[],
  views: StatementView[],
  monthsOwnedBy: MonthsOwnedBy = filedMonthOnly,
  memberCards: MemberCard[] = [],
): SplitMap => {
  // A pile of pages no statement claims belongs in the tray, never in a lane's counts.
  const held = byLane(
    views.filter((view) => !isUnfiledPile(view.statement.id)),
    accounts,
  )
  const named = accounts
    .filter(isOneNumber)
    .map((account) =>
      laneFor(account, held.get(account.id) ?? [], monthsOwnedBy),
    )
  // Statements with no account and no dates live in the tray, so an empty lane for them
  // would be a row of nothing.
  const unnamed = (held.get(NO_ACCOUNT) ?? []).some(
    (view) => view.statement.period,
  )
    ? [laneFor(null, held.get(NO_ACCOUNT) ?? [], monthsOwnedBy)]
    : []

  return {
    lanes: [...withMemberCards(named, memberCards), ...unnamed],
    unplaced: views.filter((view) => !view.statement.period),
  }
}

// Which cell a statement is drawn in, as a value that changes the moment it is re-filed.
export const monthHolding = (lanes: MapLane[], statementId: string | null) => {
  if (!statementId) return null
  for (const lane of lanes) {
    for (const year of lane.years) {
      for (const cell of year.cells) {
        const held = cell.views.some(
          (view) => view.statement.id === statementId,
        )
        if (held) return `${lane.accountId}:${year.year}:${cell.monthIndex}`
      }
    }
  }
  return null
}
